/**
 * Modal: centred overlay primitive.
 * Spec: `brand-redesign-docs/11-MODALS.md` (form modals) and
 * `08-OVERLAYS.md` (confirm). Styling lives in `wizard.css` (the split shell
 * shared with `WizardModal`) and `overlays.css` (confirm).
 *
 *   - Split shell, the Add a student look: a dark teal rail on the left (icon
 *     tile, sand `tag`, the title, an optional `note`) and a bright pane on
 *     the right (optional `chip`, the big `heading`, the `subtitle`, the
 *     body, a sticky footer). Sizes sm / md / lg set the rail and pane width.
 *   - Body scrolls inside. Footer actions are right-aligned (first child
 *     rightmost); with two or more, the last one (Cancel) sits far left.
 *   - `variant="confirm"`: no band; the caller renders a compact centred
 *     layout and names its heading with `titleId` (see ConfirmDialog).
 *   - Phones: a bottom sheet with a grab handle.
 *   - Esc closes. Backdrop click closes when `dismissOnBackdrop` (default
 *     true). Focus is trapped; body scroll is locked; renders via a portal.
 *   - Motion: transform + opacity only, gated by `useReducedMotion()`.
 */

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Sparkles, X, type LucideIcon } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { LoadGroup, OverlayLoadSlot } from "@/primitives/ui/LoadGroup";

import { useMediaQuery } from "@/hooks/useMediaQuery";

import { hasOpenPopup, isTopOverlay, popOverlay, pushOverlay } from "./stack";
import "./overlays.css";
import "./wizard.css";

export type ModalSize = "sm" | "md" | "lg";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  /** Optional muted subtitle below the title. */
  subtitle?: ReactNode;
  /** Short sand label above the title, e.g. "New lesson". */
  tag?: ReactNode;
  /** The line icon in the rail's tile. */
  icon?: LucideIcon;
  /** The pane's big heading, a short question. Falls back to `title`. */
  heading?: ReactNode;
  /** Small pill above the heading (a status, a name). */
  chip?: ReactNode;
  /** Reassurance card pinned to the bottom of the rail. */
  note?: ReactNode;
  noteIcon?: LucideIcon;
  /** `confirm`: compact card with no header band (ConfirmDialog). */
  variant?: "default" | "confirm";
  size?: ModalSize;
  children?: ReactNode;
  footer?: ReactNode;
  /** Default true. Set false for mandatory confirmation flows. */
  dismissOnBackdrop?: boolean;
  /** Default true. Set false to suppress the X icon (when only footer dismisses). */
  showCloseButton?: boolean;
  /** Override the auto-id used for aria-labelledby on the title. */
  titleId?: string;
  /** True while the modal's data loads. Header, body and footer hold as one
   *  skeleton and reveal together (10-SKELETONS). */
  loading?: boolean;
  className?: string;
  style?: CSSProperties;
}

/** A four-point sparkle, as on the wizard illustrations. */
function sparkPath(x: number, y: number, r: number) {
  return `M${x} ${y - r} Q${x + r * 0.18} ${y - r * 0.18} ${x + r} ${y} Q${x + r * 0.18} ${y + r * 0.18} ${x} ${y + r} Q${x - r * 0.18} ${y + r * 0.18} ${x - r} ${y} Q${x - r * 0.18} ${y - r * 0.18} ${x} ${y - r}Z`;
}

/** The rail's icon tile content: the line icon with two sparkles. */
function RailIcon({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <span className="md-art" aria-hidden>
      <Icon className="md-art__icon" strokeWidth={1.6} />
      <svg className="md-art__sparks" viewBox="0 0 96 96" fill="none">
        <path d={sparkPath(80, 18, 6)} className="md-art__spark md-art__spark--gold" />
        <path d={sparkPath(16, 80, 4)} className="md-art__spark" />
      </svg>
    </span>
  );
}


export function Modal({
  open,
  onClose,
  title,
  subtitle,
  tag,
  icon = Sparkles,
  heading,
  chip,
  note,
  noteIcon: NoteIcon,
  variant = "default",
  size = "md",
  children,
  footer,
  dismissOnBackdrop = true,
  showCloseButton = true,
  titleId: titleIdProp,
  loading = false,
  className,
  style,
}: ModalProps) {
  const reduce = useReducedMotion();
  const phone = !useMediaQuery("(min-width: 768px)", true);
  const isConfirm = variant === "confirm";
  const surfaceRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const autoId = useId();
  const titleId = titleIdProp ?? `modal-title-${autoId}`;

  // Register on the overlay stack while open (see `stack.ts`).
  const tokenRef = useRef<symbol | null>(null);
  useEffect(() => {
    if (!open) return;
    const token = pushOverlay();
    tokenRef.current = token;
    return () => {
      popOverlay(token);
      tokenRef.current = null;
    };
  }, [open]);

  // Esc closes + focus trap.
  useEffect(() => {
    if (!open) return;

    previousFocus.current = document.activeElement as HTMLElement | null;

    const onKey = (e: KeyboardEvent) => {
      // Only the topmost overlay answers the keyboard.
      if (!isTopOverlay(tokenRef.current)) return;
      if (e.key === "Escape") {
        // A select or date picker open inside the overlay closes first.
        if (hasOpenPopup(surfaceRef.current)) return;
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === "Tab" && surfaceRef.current) {
        trapFocus(e, surfaceRef.current);
      }
    };

    document.addEventListener("keydown", onKey);

    // Initial focus: surface itself so screen readers announce the dialog.
    // Defer one tick to allow the AnimatePresence mount.
    const t = window.setTimeout(() => {
      if (!surfaceRef.current) return;
      const focusable = firstFocusable(surfaceRef.current);
      (focusable ?? surfaceRef.current).focus({ preventScroll: true });
    }, 20);

    return () => {
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
      // Restore focus to the trigger when unmounted.
      const prev = previousFocus.current;
      if (prev && typeof prev.focus === "function") {
        prev.focus({ preventScroll: true });
      }
    };
  }, [open, onClose]);

  // Lock body scroll while any modal is open.
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  const handleBackdrop = useCallback(() => {
    if (dismissOnBackdrop) onClose();
  }, [dismissOnBackdrop, onClose]);

  if (typeof document === "undefined") return null;

  const hidden = reduce ? { opacity: 0 } : phone ? { opacity: 1, y: "100%" } : { opacity: 0, y: 12, scale: 0.97 };

  if (!isConfirm) {
    const paneHeading = heading ?? title;
    return createPortal(
      <AnimatePresence>
        {open ? (
          <div className="wz-root">
            <motion.div
              aria-hidden
              className="wz-backdrop"
              onClick={handleBackdrop}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduce ? 0 : 0.24, ease: [0.22, 1, 0.36, 1] }}
            />
            <motion.div
              ref={surfaceRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={paneHeading ? titleId : undefined}
              tabIndex={-1}
              data-size={size}
              initial={hidden}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={hidden}
              transition={
                reduce
                  ? { duration: 0 }
                  : phone
                    ? { type: "spring", stiffness: 340, damping: 34 }
                    : { duration: 0.4, ease: [0.22, 1, 0.36, 1] }
              }
              className={className ? `wz wz--modal ${className}` : "wz wz--modal"}
              style={style}
            >
              <aside className="wz-rail">
                <span className="wz-orb wz-orb--a" aria-hidden />
                <span className="wz-orb wz-orb--b" aria-hidden />
                <span className="wz-orb wz-orb--c" aria-hidden />
                <span className="grain grain-soft" aria-hidden />
                <div className="wz-rail__head">
                  <div className="wz-art">
                    <span className="wz-art__blob wz-art__blob--a" aria-hidden />
                    <span className="wz-art__blob wz-art__blob--b" aria-hidden />
                    <div className="wz-art__tile">
                      <motion.span
                        className="wz-art__inner"
                        initial={reduce ? false : { opacity: 0, scale: 0.55, rotate: -14 }}
                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                        transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 320, damping: 22, delay: 0.12 }}
                      >
                        <RailIcon icon={icon} />
                      </motion.span>
                    </div>
                  </div>
                  <div className="wz-rail__copy">
                    {tag ? <span className="wz-eyebrow">{tag}</span> : null}
                    {title ? <p className="wz-title">{title}</p> : null}
                  </div>
                </div>
                {note ? (
                  <div className="wz-note">
                    {NoteIcon ? <NoteIcon size={16} strokeWidth={1.75} aria-hidden /> : null}
                    <span>{note}</span>
                  </div>
                ) : null}
              </aside>

              <section className="wz-main">
                {showCloseButton ? (
                  <button type="button" className="wz-close" aria-label="Close" onClick={onClose}>
                    <X size={18} strokeWidth={1.75} />
                  </button>
                ) : null}
                <LoadGroup scope="overlay">
                  <OverlayLoadSlot pending={loading} />
                  <div className="wz-scroll md-scroll">
                    <div className="wz-scene">
                      {chip ? <span className="wz-chip">{chip}</span> : null}
                      {paneHeading ? (
                        <h2 id={titleId} className="wz-heading" data-chip={chip ? "" : undefined}>
                          {paneHeading}
                        </h2>
                      ) : null}
                      {subtitle ? <p className="wz-sub">{subtitle}</p> : null}
                    </div>
                    <div className="md-body" data-flush={paneHeading || subtitle ? undefined : ""}>
                      {children}
                    </div>
                  </div>
                  {footer ? <footer className="wz-foot md-foot">{footer}</footer> : null}
                </LoadGroup>
              </section>
            </motion.div>
          </div>
        ) : null}
      </AnimatePresence>,
      document.body,
    );
  }

  return createPortal(
    <AnimatePresence>
      {open ? (
        <div className="ov-root">
          <motion.div
            aria-hidden
            className="ov-backdrop"
            onClick={handleBackdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.22, ease: [0.22, 1, 0.36, 1] }}
          />

          <motion.div
            ref={surfaceRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
            data-variant="confirm"
            initial={hidden}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={hidden}
            transition={
              reduce ? { duration: 0 } : phone ? { type: "spring", stiffness: 340, damping: 34 } : { duration: 0.3, ease: [0.22, 1, 0.36, 1] }
            }
            className={className ? `ov-modal ${className}` : "ov-modal"}
            style={style}
          >
            <LoadGroup scope="overlay">
              <OverlayLoadSlot pending={loading} />
              <div style={{ overflowY: "auto", minHeight: 0 }}>
                {children}
              </div>

              {footer ? <footer className="ov-foot">{footer}</footer> : null}
            </LoadGroup>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}

/* -------------------------------------------------- Focus trap helpers */

const FOCUSABLE_SELECTOR =
  'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

function focusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(
    (el) =>
      !el.hasAttribute("disabled") &&
      el.getAttribute("aria-hidden") !== "true" &&
      el.offsetParent !== null,
  );
}

export function firstFocusable(root: HTMLElement): HTMLElement | null {
  return focusableElements(root)[0] ?? null;
}

export function trapFocus(e: KeyboardEvent, root: HTMLElement): void {
  const items = focusableElements(root);
  if (items.length === 0) {
    e.preventDefault();
    root.focus();
    return;
  }
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement as HTMLElement | null;
  if (e.shiftKey) {
    if (active === first || !root.contains(active)) {
      e.preventDefault();
      last.focus();
    }
  } else {
    if (active === last) {
      e.preventDefault();
      first.focus();
    }
  }
}
